import uiI18n from "../i18n";
import React, { useState } from "react";
import Newsletter from "./Newsletter";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  LuArrowUp,
  LuChevronDown,
  LuChevronRight,
  LuMail,
  LuMapPin,
  LuPhone,
} from "react-icons/lu";

import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa";

const POPULAR_CITIES = ["Tunis","Sfax","Sousse","Nabeul","Ariana","Monastir","Bizerte","Hammamet"];

const ALL_CITIES = ["Tunis","Ariana","Ben Arous","Manouba","Nabeul","Zaghouan","Bizerte","Béja","Jendouba","Kef","Siliana","Sousse","Monastir","Mahdia","Sfax","Kairouan","Kasserine","Sidi Bouzid","Gabès","Médenine","Tataouine","Gafsa","Tozeur","Kébili","Hammamet"];

const NAVIGATION_LINKS = [
  {
    translationKey: "home",
    get fallback() { return uiI18n.t("ui.home"); },
    path: "/",
  },
  {
    translationKey: "buy",
    get fallback() { return uiI18n.t("ui.buy"); },
    path: "/Category/sale",
  },
  {
    translationKey: "rent",
    get fallback() { return uiI18n.t("ui.rent_308"); },
    path: "/Category/rent",
  },
  {
    translationKey: "vacances",
    get fallback() { return uiI18n.t("ui.holidays"); },
    path: "/Vacation",
  },
  {
    translationKey: "offers",
    get fallback() { return uiI18n.t("offers"); },
    path: "/offers",
  },
];

const INFORMATION_LINKS = [
  {
    get label() { return uiI18n.t("ui.about_us"); },
    path: "/about",
  },
  {
    get label() { return uiI18n.t("ui.contact"); },
    path: "/contact",
  },
  {
    get label() { return uiI18n.t("ui.terms_of_use"); },
    path: "/terms",
  },
  {
    get label() { return uiI18n.t("ui.privacy_policy"); },
    path: "/privacy",
  },
];

const SOCIAL_LINKS = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61594836002130",
    icon: FaFacebookF,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/lglinaimmo/",
    icon: FaInstagram,
  },
  {
    label: "LinkedIn",
    href: "https://linkedin.com",
    icon: FaLinkedinIn,
  },
  {
    label: "YouTube",
    href: "https://youtube.com",
    icon: FaYoutube,
  },
];

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showAllCities, setShowAllCities] = useState(false);

  const displayedCities = showAllCities
    ? ALL_CITIES
    : POPULAR_CITIES;

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleNavigation = (path) => {
    navigate(path);
    scrollToTop();
  };

  const handleCityClick = (city) => {
    navigate(
      `/categorybyville?city=${encodeURIComponent(city)}`
    );

    scrollToTop();
  };

  return (
    <footer className="relative overflow-hidden bg-slate-950 text-slate-300">
      {/* Décoration de fond */}
      <div className="pointer-events-none absolute -start-40 top-0 h-96 w-96 rounded-full bg-green-500/5 blur-3xl" />

      <div className="pointer-events-none absolute -end-40 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <Newsletter />
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {/* Marque */}
          <section>
            <button
              type="button"
              onClick={() => handleNavigation("/")}
              aria-label={uiI18n.t("ui.back_to_home")}
              className="group text-start"
            >
              <span className="block bg-gradient-to-r from-white via-indigo-200 to-purple-300 bg-clip-text text-2xl font-black italic tracking-tight text-transparent">
                LINA IMMO
              </span>

              <span className="mt-1 block text-[11px] text-slate-500"> {uiI18n.t("ui.rent_invest_negotiate_buy")} </span>
            </button>

            <p className="mt-5 max-w-sm text-sm leading-7 text-slate-400"> {uiI18n.t("ui.find_your_next_home_list_your_property_and_discover_real_estate_opportunities_across_tunisia")} </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {SOCIAL_LINKS.map((social) => {
                const Icon = social.icon;

                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    title={social.label}
                    className="
                      flex
                      h-10
                      w-10
                      items-center
                      justify-center
                      rounded-xl
                      border
                      border-white/10
                      bg-white/5
                      text-slate-400
                      transition-all
                      duration-200
                      hover:-translate-y-0.5
                      hover:border-green-500/40
                      hover:bg-green-500/10
                      hover:text-green-400
                    "
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </section>

          {/* Navigation */}
          <section>
            <h3 className="text-base font-bold text-white"> {uiI18n.t("ui.navigation")} </h3>

            <ul className="mt-5 space-y-3">
              {NAVIGATION_LINKS.map((item) => (
                <li key={item.path}>
                  <button
                    type="button"
                    onClick={() =>
                      handleNavigation(item.path)
                    }
                    className="
                      group
                      flex
                      items-center
                      gap-2
                      text-sm
                      text-slate-400
                      transition
                      hover:text-green-400
                    "
                  >
                    <LuChevronRight
                      size={14}
                      className="text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-green-500 rtl:rotate-180"
                    />

                    <span>
                      {t(
                        item.translationKey,
                        item.fallback
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-8 space-y-3">
              {INFORMATION_LINKS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path} onClick={scrollToTop}
                  className="block text-start text-sm text-slate-500 transition hover:text-white"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </section>

          {/* Villes */}
          <section>
            <h3 className="text-base font-bold text-white">
              {t(
                "details_vacation.our_cities",
                "Explorer par ville"
              )}
            </h3>

            <p className="mt-2 text-sm leading-6 text-slate-500"> {uiI18n.t("ui.discover_available_properties_in_the_most_popular_cities")} </p>

            <div className="mt-5 flex flex-wrap gap-2">
              {displayedCities.map((city) => (
                <button
                  key={city}
                  type="button"
                  onClick={() => handleCityClick(city)}
                  className="
                    rounded-xl
                    border
                    border-white/10
                    bg-white/5
                    px-3
                    py-2
                    text-xs
                    font-medium
                    text-slate-300
                    transition-all
                    duration-200
                    hover:border-green-500/40
                    hover:bg-green-500/10
                    hover:text-green-400
                  "
                >
                  {t(`cities.${city}`, city)}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() =>
                setShowAllCities(
                  (previousValue) => !previousValue
                )
              }
              aria-expanded={showAllCities}
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-green-400 transition hover:text-green-300"
            >
              <span>
                {showAllCities
                  ? uiI18n.t("ui.show_less")
                  : uiI18n.t("ui.view_all_cities")}
              </span>

              <LuChevronDown
                size={16}
                className={`transition-transform duration-300 ${
                  showAllCities ? "rotate-180" : ""
                }`}
              />
            </button>
          </section>

          {/* Contact et newsletter */}
          <section>
            <h3 className="text-base font-bold text-white"> {uiI18n.t("ui.keep_in_touch")} </h3>
            Lassaad GHERIBI
            <div className="mt-5 space-y-4">
            

              <a
                href="mailto:lassaadgueribi@gmail.com"
                className="group flex items-start gap-3"
                
              > 
              
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-green-400 transition group-hover:bg-green-500/10">
                  <LuMail size={17} />
                </span>
                 
                <span className="min-w-0">
                  <span className="block text-xs text-slate-500"> {uiI18n.t("ui.email_94")} </span>

                  <span className="block break-all text-sm text-slate-300 transition group-hover:text-white">
                    lassaadgueribi@gmail.com
                  </span>
                </span>
              </a>

              <a
                href="tel:+33605988954"
                className="group flex items-start gap-3"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-green-400 transition group-hover:bg-green-500/10">
                  <LuPhone size={17} />
                </span>

                <span>
                  <span className="block text-xs text-slate-500"> {uiI18n.t("ui.phone")} </span>
                  <span className="text-sm text-slate-300 transition group-hover:text-white">
                    <span dir="ltr">(+33) 06 05 98 89 54</span>
                  </span>
                </span>
              </a>
              <a href="https://wa.me/33605988954" target="_blank" rel="noopener noreferrer" className="group flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-green-400"><LuPhone size={17} /></span>
                <span>
                  <span className="block text-xs text-slate-500"> WhatsApp </span>
                  <span className="text-sm text-slate-300 transition group-hover:text-white">
                    <span dir="ltr">(+33) 06 05 98 89 54</span>
                  </span>
                </span>
              </a>

              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-green-400">
                  <LuMapPin size={17} />
                </span>

                <span>
                  <span className="block text-xs text-slate-500"> {uiI18n.t("ui.address")} </span> 
                  <span className="text-sm text-slate-300"> {uiI18n.t("ui.kairouan_tunisia")} </span><br></br>
                 </span>
              </div>
            </div>


          </section>
        </div>

        {/* Partie inférieure */}
        <div className="mt-12 flex flex-col gap-5 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-center text-sm text-slate-500 sm:text-start">
            © {new Date().getFullYear()} {uiI18n.t("ui.lina_immo_all_rights_reserved")} </p>

          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500 sm:justify-end">
            <button
              type="button"
              onClick={() =>
                handleNavigation("/privacy")
              }
              className="transition hover:text-white"
            > {uiI18n.t("ui.privacy")} </button>

            <span className="h-1 w-1 rounded-full bg-slate-700" />

            <button
              type="button"
              onClick={() =>
                handleNavigation("/terms")
              }
              className="transition hover:text-white"
            > {uiI18n.t("ui.terms")} </button>
          </div>
        </div>
      </div>

      {/* Retour en haut */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label={uiI18n.t("ui.back_to_top")}
        title={uiI18n.t("ui.back_to_top")}
        className="
          absolute
          bottom-5
          end-4
          flex
          h-11
          w-11
          items-center
          justify-center
          rounded-2xl
          border
          border-white/10
          bg-white/10
          text-white
          shadow-xl
          backdrop-blur
          transition-all
          hover:-translate-y-1
          hover:border-green-500/40
          hover:bg-green-600
          focus:outline-none
          focus:ring-4
          focus:ring-green-500/20
          sm:end-6
          lg:end-8
        "
      >
        <LuArrowUp size={17} />
      </button>
    </footer>
  );
}